// src/pages/CartPage.js
import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { usePriceVisibility } from "../context/PriceVisibilityContext";
import CartItem from "../components/CartItem";
import { useCart } from "../context/CartContext";
import { apiRequest } from "../services/api";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import { getAllProducts, getProductBySlug } from "../services/products.api";
import { getActiveCouponsCount } from "../services/coupons.api";
import {
  getActiveOrderDiscount,
  getActiveFreeShippingDiscount,
} from "../services/discounts.api";
import { mapBackendProduct } from "../utils/mapProduct";

function CartPage() {
  const { lang } = useContext(LanguageContext);
  const { pricesSalesVisible } = usePriceVisibility();
  const navigate = useNavigate();
  const location = useLocation();
   const {
  cartItems,
  cartLoading,
  cartShareToken,
  subtotal,
  discount,
  total,
  addToCart,
  removeFromCart,
  changeQty,
  updateCartItem,
  bxgySuggestions,
  addBxgyDiscountItem,
} = useCart();

  const isEmpty = cartItems.length === 0;

  const [shipping, setShipping] = useState({
    fee: 4,
    free_enabled: false,
    free_threshold: 0,
  });

  const [couponOpen, setCouponOpen] = useState(false);
const [couponInput, setCouponInput] = useState("");
const [cartCoupon, setCartCoupon] = useState(null);
const [couponLoading, setCouponLoading] = useState(false);
const [couponError, setCouponError] = useState(null);
const [hasActiveCoupons, setHasActiveCoupons] = useState(true);

const [orderDiscount, setOrderDiscount] = useState(null);
const [freeShippingDiscount, setFreeShippingDiscount] = useState(null);

const [editItemId, setEditItemId] = useState(null);
const [editProduct, setEditProduct] = useState(null);
const [editLoading, setEditLoading] = useState(false);
const [editColor, setEditColor] = useState("");
const [editVariant, setEditVariant] = useState(null);
const [editQty, setEditQty] = useState(1);
const [editError, setEditError] = useState("");

const [recommendedProducts, setRecommendedProducts] = useState([]);
const recommendedRailRef = useRef(null);
const helpCheckoutRef = useRef(null);
const sharedBackGuardRef = useRef(false);
const [showFixedCheckout, setShowFixedCheckout] = useState(true);

const [recommendedAddingId, setRecommendedAddingId] = useState(null);
const [recommendedAddedModal, setRecommendedAddedModal] = useState(null);
const [bxgySuggestionAddingKey, setBxgySuggestionAddingKey] = useState(null);

  const freeItems = cartItems.filter((item) => item.isBxgyFree);

const normalItems = cartItems.filter(
  (item) => !item.isBxgyFree && !item.isBxgyDiscounted
);

const discountedItems = cartItems.filter(
  (item) => item.isBxgyDiscounted
);

  const getSafeStockQty = (item) => {
  const rawStock =
    item.stock_qty ??
    item.stock ??
    item.variant_stock ??
    item.available_stock ??
    null;

  if (rawStock === null || rawStock === undefined || rawStock === "") {
    return null;
  }

  const stock = Number(rawStock);
  return Number.isFinite(stock) ? stock : null;
};

const hasOutOfStock = cartItems.some((item) => {
  const stock = getSafeStockQty(item);

  // Do not block checkout if backend did not send stock info
  if (stock === null) return false;

  return stock <= 0;
});

const hasQtyExceedStock = cartItems.some((item) => {
  const stock = getSafeStockQty(item);

  // Do not block checkout if backend did not send stock info
  if (stock === null) return false;

  return stock > 0 && Number(item.qty || 0) > stock;
});

const disableCheckout = hasOutOfStock || hasQtyExceedStock || !pricesSalesVisible;

  const TEXT = {
    EN: {
      title: "YOUR CART",
      empty: "Your cart is empty.",
      continue: "Continue Shopping",
      wishlist: "View Wishlist",
      subtotal: "Subtotal:",
      total: "Total:",
      checkout: "PROCEED TO CHECKOUT",
pricesHiddenCheckout:
  "Checkout is currently disabled because prices and sales are hidden.",
priceHidden: "Price hidden",
      deliveryInfo: "Delivery fees",
      freeDeliveryInfo: "Free delivery on orders above",
      pickupInfo: "Pick up from store",
    },
    AR: {
      title: "سلة المشتريات",
      empty: "سلة المشتريات فارغة.",
      continue: "متابعة التسوق",
      wishlist: "عرض المفضلة",
      subtotal: "المجموع:",
      total: "الإجمالي:",
      checkout: "إتمام الشراء",
pricesHiddenCheckout:
  "الدفع غير متاح حالياً لأن الأسعار والعروض مخفية.",
priceHidden: "السعر مخفي",
      deliveryInfo: "رسوم التوصيل",
      freeDeliveryInfo: "توصيل مجاني للطلبات فوق",
      pickupInfo: "استلام من المحل",
    },
  };

  const t = TEXT[lang];

  useEffect(() => {
  if (cartLoading) return;
  if (!cartShareToken) return;

  const params = new URLSearchParams(location.search);
  const existingToken = params.get("cart_token");

  if (existingToken === cartShareToken) return;

  params.set("cart_token", cartShareToken);

  const nextUrl = `${location.pathname}?${params.toString()}`;

  window.history.replaceState(null, "", nextUrl);
}, [cartLoading, cartShareToken, location.pathname, location.search]);

useEffect(() => {
  if (cartLoading) return;
  if (sharedBackGuardRef.current) return;
  if (location.pathname !== "/cart") return;

  const params = new URLSearchParams(window.location.search);
  const hasCartToken = Boolean(params.get("cart_token"));

  if (!hasCartToken) return;

  const currentState = window.history.state || {};
  const currentIdx =
    typeof currentState.idx === "number" ? currentState.idx : 0;

  // Normal navigation from inside the website should stay normal.
  if (currentIdx > 0) return;

  sharedBackGuardRef.current = true;
  sessionStorage.setItem("velle-direct-shared-cart", "1");

  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash || ""}`;

  window.history.replaceState(
    {
      ...(window.history.state || {}),
      __velleSharedOrigin: "cart",
    },
    "",
    currentUrl
  );

  window.history.pushState(
    {
      ...(window.history.state || {}),
      __velleSharedGuard: "cart",
    },
    "",
    currentUrl
  );

  const handlePopState = (event) => {
    if (
      event.state?.__velleSharedOrigin === "cart" ||
      sessionStorage.getItem("velle-direct-shared-cart") === "1"
    ) {
      sessionStorage.removeItem("velle-direct-shared-cart");
      navigate("/", { replace: true });
    }
  };

  window.addEventListener("popstate", handlePopState);

  return () => {
    window.removeEventListener("popstate", handlePopState);
  };
}, [cartLoading, cartShareToken, location.pathname, navigate]);

  useEffect(() => {
  if (isEmpty) return;

  const target = helpCheckoutRef.current;
  if (!target) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      setShowFixedCheckout(!entry.isIntersecting);
    },
    {
      root: null,
      threshold: 0.35,
    }
  );

  observer.observe(target);

  return () => observer.disconnect();
}, [isEmpty]);

  useEffect(() => {
    apiRequest("/shipping-settings")
      .then((s) =>
        setShipping({
          fee: Number(s.default_fee ?? 4),
          free_enabled: !!s.free_enabled,
          free_threshold: Number(s.free_threshold ?? 0),
        })
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
  getActiveCouponsCount()
    .then((res) => setHasActiveCoupons(Number(res.count || 0) > 0))
    .catch(() => setHasActiveCoupons(false));
}, []);

useEffect(() => {
  getActiveOrderDiscount()
    .then(setOrderDiscount)
    .catch(() => setOrderDiscount(null));
}, []);

useEffect(() => {
  getActiveFreeShippingDiscount()
    .then(setFreeShippingDiscount)
    .catch(() => setFreeShippingDiscount(null));
}, []);

  useEffect(() => {
  let mounted = true;

  getAllProducts({})
    .then((rows) => {
      if (!mounted) return;

      const mapped = Array.isArray(rows)
        ? rows.map(mapBackendProduct)
        : [];

      const cartProductIds = new Set(
        cartItems.map((item) => Number(item.product_id))
      );

      const cartProducts = mapped.filter((p) =>
        cartProductIds.has(Number(p.id))
      );

      const baseProduct = cartProducts[0] || null;

      const getMainCategory = (p) => {
        if (!p) return "";
        if (p.primary_category) return String(p.primary_category).toLowerCase();
        if (p.category) return String(p.category).toLowerCase();
        if (Array.isArray(p.categories) && p.categories[0]) {
          return String(p.categories[0]).toLowerCase();
        }
        return "";
      };

      const getTags = (p) =>
        Array.isArray(p?.tags)
          ? p.tags.map((tag) => String(tag || "").toLowerCase()).filter(Boolean)
          : [];

      const baseCategory = getMainCategory(baseProduct);
      const baseTags = new Set(getTags(baseProduct));

      const candidates = mapped.filter(
        (p) => !cartProductIds.has(Number(p.id))
      );

      const mutualDifferentCategory = candidates
        .map((p) => {
          const productCategory = getMainCategory(p);
          const mutualCount = getTags(p).filter((tag) => baseTags.has(tag)).length;

          return {
            ...p,
            __mutualCount: mutualCount,
            __differentCategory:
              baseCategory && productCategory && productCategory !== baseCategory,
          };
        })
        .filter((p) => p.__differentCategory && p.__mutualCount > 0)
        .sort((a, b) => b.__mutualCount - a.__mutualCount);

      const fallbackDifferentCategory = candidates
        .filter((p) => {
          const productCategory = getMainCategory(p);
          return baseCategory && productCategory && productCategory !== baseCategory;
        })
        .filter(
          (p) =>
            !mutualDifferentCategory.some(
              (x) => Number(x.id) === Number(p.id)
            )
        );

      const fallbackAny = candidates.filter(
        (p) =>
          !mutualDifferentCategory.some((x) => Number(x.id) === Number(p.id)) &&
          !fallbackDifferentCategory.some((x) => Number(x.id) === Number(p.id))
      );

      const finalList = [
        ...mutualDifferentCategory,
        ...fallbackDifferentCategory,
        ...fallbackAny,
      ].slice(0, 10);

      setRecommendedProducts(finalList);
    })
    .catch(() => {
      if (mounted) setRecommendedProducts([]);
    });

  return () => {
    mounted = false;
  };
}, [cartItems]);

useEffect(() => {
  let mounted = true;

  const item = cartItems.find((x) => Number(x.id) === Number(editItemId));

  if (!item?.slug) {
    setEditProduct(null);
    setEditError("");
    return;
  }

  setEditLoading(true);
  setEditError("");

  getProductBySlug(item.slug)
    .then((product) => {
      if (!mounted) return;

      setEditProduct(product);

      const matchingVariant =
        product?.variants?.find(
          (v) => Number(v.id) === Number(item.variant_id)
        ) || null;

      const firstAvailable =
        product?.variants?.find((v) => Number(v.stock_qty) > 0) || null;

      const selected = matchingVariant || firstAvailable;

      setEditColor(selected?.color_name || "");
      setEditVariant(selected || null);
      setEditQty(Math.min(Number(item.qty || 1), Number(selected?.stock_qty || 1)));
    })
    .catch(() => {
      if (mounted) {
        setEditProduct(null);
        setEditError(
          lang === "AR"
            ? "تعذر تحميل خيارات المنتج"
            : "Could not load product options"
        );
      }
    })
    .finally(() => {
      if (mounted) setEditLoading(false);
    });

  return () => {
    mounted = false;
  };
}, [editItemId, cartItems, lang]);

  const formatPrice = (value) => {
  const n = Number(value || 0);
  return `$${n.toFixed(2)}`;
};

const totalItemsCount = cartItems.reduce(
  (sum, item) => sum + Number(item.qty || 0),
  0
);

const getOrderDiscountAmount = () => {
  if (!orderDiscount) return 0;

  const subtotalValue = Number(subtotal || 0);

  let valid = false;

  if (orderDiscount.min_requirement === "none") {
    valid = true;
  }

  if (
    orderDiscount.min_requirement === "amount" &&
    subtotalValue >= Number(orderDiscount.min_amount || 0)
  ) {
    valid = true;
  }

  if (
    orderDiscount.min_requirement === "qty" &&
    totalItemsCount >= Number(orderDiscount.min_qty || 0)
  ) {
    valid = true;
  }

  if (!valid) return 0;

  if (orderDiscount.discount_type === "percentage") {
    return Math.min(
      (subtotalValue * Number(orderDiscount.discount_value || 0)) / 100,
      subtotalValue
    );
  }

  if (orderDiscount.discount_type === "fixed") {
    return Math.min(
      Number(orderDiscount.discount_value || 0),
      subtotalValue
    );
  }

  return 0;
};

const getFreeShippingDiscountInfo = () => {
  if (!freeShippingDiscount) {
    return {
      valid: false,
      label: "",
      endLabel: "",
    };
  }

  const subtotalValue = Number(subtotal || 0);
  const minRequirement = freeShippingDiscount.min_requirement || "none";
  const minAmount = Number(freeShippingDiscount.min_amount || 0);
  const minQty = Number(freeShippingDiscount.min_qty || 0);

  let valid = false;
  let label = "";

  if (minRequirement === "none") {
    valid = true;
    label =
      lang === "AR"
        ? "شحن مجاني على جميع الطلبات"
        : "Free delivery on all orders";
  }

  if (minRequirement === "amount") {
    valid = subtotalValue >= minAmount;

    const missingAmount = Math.max(0, minAmount - subtotalValue);

    label = valid
      ? lang === "AR"
        ? `تم تفعيل الشحن المجاني لأن طلبك وصل إلى $${minAmount}`
        : `Free delivery unlocked on orders over $${minAmount}`
      : lang === "AR"
      ? `أضيفي $${missingAmount.toFixed(2)} للحصول على شحن مجاني`
      : `Add $${missingAmount.toFixed(2)} more for free delivery`;
  }

  if (minRequirement === "qty") {
    valid = totalItemsCount >= minQty;

    const missingQty = Math.max(0, minQty - totalItemsCount);

    label = valid
      ? lang === "AR"
        ? `تم تفعيل الشحن المجاني عند شراء ${minQty}+ منتجات`
        : `Free delivery unlocked for ${minQty}+ items`
      : lang === "AR"
      ? `أضيفي ${missingQty} منتجات للحصول على شحن مجاني`
      : `Add ${missingQty} more item${missingQty === 1 ? "" : "s"} for free delivery`;
  }

  const endLabel = freeShippingDiscount.end_at
    ? new Date(freeShippingDiscount.end_at).toLocaleString(
        lang === "AR" ? "ar" : "en",
        {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        }
      )
    : "";

  return {
    valid,
    label,
    endLabel,
  };
};

const couponDiscount = Number(cartCoupon?.discount || 0);

const safeOrderDiscountAmount = Number(getOrderDiscountAmount() || 0);

// Coupon should not stack with automatic order discount.
const effectiveOrderDiscountAmount = cartCoupon
  ? 0
  : safeOrderDiscountAmount;

const freeShippingInfo = getFreeShippingDiscountInfo();

const isFreeDeliveryFromSettings =
  shipping.free_enabled &&
  Number(subtotal || 0) >= Number(shipping.free_threshold || 0);

const isFreeDeliveryFromDiscount = freeShippingInfo.valid;

const isFreeDelivery =
  isFreeDeliveryFromSettings || isFreeDeliveryFromDiscount;

const deliveryFee = isFreeDelivery ? 0 : Number(shipping.fee || 0);

const cartSummaryTotal = Math.max(
  0,
  Number(subtotal || 0) -
    effectiveOrderDiscountAmount -
    couponDiscount +
    Number(deliveryFee || 0)
);

const getCartItemName = (item) =>
  lang === "AR" ? item.nameAR || item.nameEN : item.nameEN || item.nameAR;

const getCartItemColorHex = (item) =>
  item.color_hex || item.colorHex || item.hex || "#000000";

const getCartItemFinalPrice = (item) => Number(item.price || 0);

const getCartItemBasePrice = (item) => {
  const price = Number(item.price || 0);
  const discountValue = Number(item.bxgyDiscountValue || 0);

  if (item.isBxgyDiscounted && discountValue > 0) {
    return Math.round(price / (1 - discountValue / 100));
  }

  return price;
};

const getRecommendedName = (product) =>
  lang === "AR"
    ? product.nameAR || product.nameEN
    : product.nameEN || product.nameAR;

const getRecommendedFinalPrice = (product) => {
  const base = Number(product.price || 0);
  const sale = Number(product.discount || 0);

  if (sale > 0) {
    return Math.round(base * (1 - sale / 100));
  }

  return base;
};

const resolveRecommendedMedia = (product) => {
  const first = product?.images?.[0];

  if (!first) return "/images/placeholder-product.jpg";

  if (typeof first === "string") return first;

  return first.url || first.image || first.image_url || "/images/placeholder-product.jpg";
};

const isRecommendedVideo = (product) => {
  const first = product?.images?.[0];
  const url = resolveRecommendedMedia(product);
  const type = typeof first === "object" ? first.media_type : "";

  return type === "video" || /\.(mp4|webm|ogg|mov)$/i.test(url.split("?")[0]);
};

const goToRecommendedProduct = (product) => {
  const slug = product?.slug || product?.seo_slug;
  if (!slug) return;

  navigate(`/product/${encodeURIComponent(slug)}`);
};

const scrollRecommended = (direction) => {
  if (!recommendedRailRef.current) return;

  recommendedRailRef.current.scrollBy({
    left: direction === "next" ? 260 : -260,
    behavior: "smooth",
  });
};

const applyMobileCoupon = async () => {
  const code = String(couponInput || "").trim();

  if (!code) return;

  setCouponLoading(true);
  setCouponError(null);

  try {
    const token = localStorage.getItem("esill-token");

const res = await apiRequest("/coupons/validate", {
  method: "POST",
  headers: token
    ? { Authorization: `Bearer ${token}` }
    : {},
  body: JSON.stringify({
    code,
    subtotal: Number(subtotal || 0),
  }),
});

    setCartCoupon(res);
    setCouponInput(res.code || code);
  } catch (err) {
    setCartCoupon(null);

    if (err?.status === 400) {
      if (err.message === "expired") {
        setCouponError("expired");
      } else {
        setCouponError("unavailable");
      }
    } else {
      setCouponError("unavailable");
    }
  } finally {
    setCouponLoading(false);
  }
};

const clearMobileCoupon = () => {
  setCartCoupon(null);
  setCouponError(null);
  setCouponInput("");
};

const getItemSalePercent = (item) => Number(item.sale_percent || 0);

const itemHasSale = (item) =>
  !!item.on_sale && getItemSalePercent(item) > 0;

const itemHasBxgyPercentageDiscount = (item) =>
  item.isBxgyDiscounted && Number(item.bxgyDiscountValue || 0) > 0;

const itemHasAnyPriceDiscount = (item) =>
  itemHasSale(item) || itemHasBxgyPercentageDiscount(item);

const getItemDisplayDiscountPercent = (item) => {
  if (itemHasSale(item)) return getItemSalePercent(item);
  if (itemHasBxgyPercentageDiscount(item)) {
    return Number(item.bxgyDiscountValue || 0);
  }
  return 0;
};

const getCartItemCompareTotal = (item) => {
  if (itemHasBxgyPercentageDiscount(item)) {
    return getCartItemBasePrice(item) * Number(item.qty || 1);
  }

  return getCartItemOriginalTotal(item);
};

const getCartItemOriginalUnitPrice = (item) =>
  Number(item.original_price || item.base_price || item.price || 0);

const getCartItemOriginalTotal = (item) =>
  getCartItemOriginalUnitPrice(item) * Number(item.qty || 1);

const getCartItemFinalTotal = (item) =>
  Number(item.price || 0) * Number(item.qty || 1);

const isItemInvalid = (item) => {
  const stock = getSafeStockQty(item);

  // Do not show red ribbon if backend did not send stock info
  if (stock === null) return false;

  return stock <= 0 || Number(item.qty || 0) > stock;
};

const getEditColors = () => {
  const variants = Array.isArray(editProduct?.variants)
    ? editProduct.variants
    : [];

  const map = new Map();

  variants.forEach((v) => {
    if (!v.color_name) return;

    if (!map.has(v.color_name)) {
      map.set(v.color_name, {
        name: v.color_name,
        code: v.color_hex || "#000000",
      });
    }
  });

  return Array.from(map.values());
};

const getEditSizes = () => {
  const variants = Array.isArray(editProduct?.variants)
    ? editProduct.variants
    : [];

  return variants.filter((v) => v.color_name === editColor);
};

const getEditColorImage = (colorName) => {
  const images = Array.isArray(editProduct?.images)
    ? editProduct.images
    : [];

  const normalizedColor = String(colorName || "")
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[-_]/g, "");

  const found =
    images.find((img) => {
      const imgColor = String(img.color_name || "")
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[-_]/g, "");

      return imgColor && imgColor === normalizedColor;
    }) || images[0];

  if (!found) return "";

  if (typeof found === "string") return found;

  return found.url || found.image || found.image_url || "";
};

const applyCartItemEdit = async (item) => {
  if (!editVariant) {
    setEditError(
      lang === "AR" ? "يرجى اختيار المقاس" : "Please select a size"
    );
    return;
  }

  const maxQty = Number(editVariant.stock_qty || 0);
  const safeQty = Math.min(Math.max(1, Number(editQty || 1)), maxQty);

  if (maxQty <= 0 || safeQty <= 0) {
    setEditError(
      lang === "AR"
        ? "هذا الخيار غير متوفر"
        : "This option is out of stock"
    );
    return;
  }

  await updateCartItem(item, {
  variant_id: Number(editVariant.id),
  qty: safeQty,
});

  setEditItemId(null);
  setEditProduct(null);
  setEditError("");
};

const ChevronDownIcon = ({ open = false }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={`
      w-[22px] h-[22px]
      text-black
      transition-transform duration-200
      ${open ? "rotate-180" : ""}
    `}
    aria-hidden="true"
  >
    <path
      d="M6.5 9L12 14.5L17.5 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="w-[19px] h-[19px]">
    <path
      d="M4 7H20"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
    />
    <path
      d="M9 7V5.5C9 4.7 9.7 4 10.5 4H13.5C14.3 4 15 4.7 15 5.5V7"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
    />
    <path
      d="M7 7L8 20H16L17 7"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinejoin="round"
    />
    <path
      d="M10.5 11V17"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M13.5 11V17"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="w-[34px] h-[34px]">
    <rect x="7" y="2.8" width="10" height="18.4" rx="1.8" stroke="currentColor" strokeWidth="1.6" />
    <path d="M10 18.3H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const TruckIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-[30px] h-[30px]">
    <path d="M3 10H19V22H3V10Z" stroke="currentColor" strokeWidth="1.6" />
    <path d="M19 15H25L29 19V22H19V15Z" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="9" cy="24" r="2.2" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="24" cy="24" r="2.2" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-[30px] h-[30px]">
    <path
      d="M16 4L26 8V15C26 21.5 21.7 26 16 28C10.3 26 6 21.5 6 15V8L16 4Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-[26px] h-[26px]">
    <rect x="8" y="14" width="16" height="12" rx="1.8" stroke="currentColor" strokeWidth="1.7" />
    <path d="M11 14V10C11 7.2 13.2 5 16 5C18.8 5 21 7.2 21 10V14" stroke="currentColor" strokeWidth="1.7" />
    <path d="M16 18.5V22" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const getBxgySuggestionKey = (suggestion, index = 0) => {
  return [
    suggestion.action || "bxgy",
    suggestion.buyProductId || 0,
    suggestion.buyVariantId || 0,
    suggestion.getProductId || 0,
    suggestion.discountType || "none",
    suggestion.discountValue || 0,
    index,
  ].join("-");
};

const getBxgyUpsellText = (suggestion) => {
  if (!suggestion) return "";

  const discountText =
    suggestion.discountType === "free"
      ? lang === "AR"
        ? "مجاناً"
        : "FREE"
      : lang === "AR"
      ? `بخصم ${suggestion.discountValue}%`
      : `${suggestion.discountValue}% OFF`;

  if (suggestion.action === "complete_percentage_same_product") {
    return lang === "AR"
      ? `أضيفي ${suggestion.missingQty} أكثر واحصلي على الأخيرة ${discountText}`
      : `Add ${suggestion.missingQty} more to get the last one ${discountText}`;
  }

  if (suggestion.action === "add_buy_more") {
    return lang === "AR"
      ? `أضيفي ${suggestion.missingQty} أكثر للحصول على ${suggestion.getQty} ${discountText}`
      : `Add ${suggestion.missingQty} more to get ${suggestion.getQty} ${discountText}`;
  }

  return lang === "AR"
    ? `أضيفي ${suggestion.getQty} إلى السلة ${discountText}`
    : `Add ${suggestion.getQty} to cart ${discountText}`;
};

const cartBxgySuggestions = useMemo(() => {
  return (Array.isArray(bxgySuggestions) ? bxgySuggestions : []).filter((s) => {
    if (!s?.action) return false;

    const hasNormalBuyItem = cartItems.some(
      (item) =>
        Number(item.product_id) === Number(s.buyProductId) &&
        (!s.buyVariantId ||
          Number(item.variant_id) === Number(s.buyVariantId)) &&
        (item.bxgyType || "none") === "none"
    );

    if (!hasNormalBuyItem) return false;

    if (
      s.action === "add_buy_more" ||
      s.action === "complete_percentage_same_product"
    ) {
      return Number(s.missingQty || 0) > 0;
    }

    return true;
  });
}, [bxgySuggestions, cartItems]);

const handleCartBxgySuggestion = async (suggestion, index) => {
  const key = getBxgySuggestionKey(suggestion, index);

  if (bxgySuggestionAddingKey) return;

  try {
    setBxgySuggestionAddingKey(key);
    await addBxgyDiscountItem(suggestion);
  } catch (err) {
    console.error(err);
  } finally {
    setBxgySuggestionAddingKey(null);
  }
};

const CartBxgySuggestions = ({ desktop = false }) => {
  if (!cartBxgySuggestions.length) return null;

  return (
    <div
      className={`
        ${desktop ? "mt-8" : "mt-6"}
        border border-green-200
        bg-green-50
        px-4
        py-4
      `}
    >
      <p
        className={`
          font-semibold
          text-green-900
          ${desktop ? "text-[14px]" : "text-[16px]"}
        `}
      >
        {lang === "AR" ? "عرض خاص متاح" : "Special offer available"}
      </p>

      <div className="mt-3 space-y-3">
        {cartBxgySuggestions.map((suggestion, index) => {
          const key = getBxgySuggestionKey(suggestion, index);
          const loading = bxgySuggestionAddingKey === key;

          return (
            <button
              key={key}
              type="button"
              disabled={loading}
              onClick={() => handleCartBxgySuggestion(suggestion, index)}
              className={`
                w-full
                bg-green-700
                text-white
                font-semibold
                rounded-md
                hover:bg-green-800
                transition
                disabled:opacity-60
                disabled:cursor-wait
                ${desktop ? "h-11 text-[13px]" : "min-h-[50px] px-3 text-[15px]"}
              `}
            >
              {loading
                ? lang === "AR"
                  ? "جارٍ الإضافة..."
                  : "Adding..."
                : getBxgyUpsellText(suggestion)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const cartDisplayItems = [...normalItems, ...discountedItems, ...freeItems];


const getRecommendedProductUrl = (product) => {
  const slug = product?.slug || product?.seo_slug;
  if (!slug) return "#";
  return `/product/${encodeURIComponent(slug)}`;
};

const getRecommendedTotalStock = (product) =>
  Number(product?.totalStock || product?.total_stock || product?.stock || 0);

const getRecommendedSizes = (product) =>
  Array.isArray(product?.sizes) && product.sizes.length
    ? product.sizes
    : Array.isArray(product?.variants)
    ? product.variants
        .map((v) => v.size)
        .filter((size, index, arr) => size && arr.indexOf(size) === index)
    : [];

const getRecommendedColors = (product) =>
  Array.isArray(product?.colors) && product.colors.length
    ? product.colors
    : Array.isArray(product?.variants)
    ? product.variants
        .filter((v) => v.color_name || v.color_hex)
        .reduce((acc, v) => {
          const key = `${v.color_name || ""}-${v.color_hex || ""}`;
          if (!acc.some((x) => x.key === key)) {
            acc.push({
              key,
              name: v.color_name || "",
              code: v.color_hex || "#8B3A2F",
            });
          }
          return acc;
        }, [])
    : [];

const shouldCheckRecommendedAvailability = (product) => {
  const totalStock = getRecommendedTotalStock(product);
  const sizeBadges = getRecommendedSizes(product);
  const colorBadges = getRecommendedColors(product);

  return totalStock <= 0 || sizeBadges.length === 0 || colorBadges.length === 0;
};

const handleRecommendedAddToCart = async (e, product) => {
  e.stopPropagation();

  if (shouldCheckRecommendedAvailability(product)) {
    goToRecommendedProduct(product);
    return;
  }

  if (recommendedAddingId) return;

  setRecommendedAddingId(product.id);

  try {
    const slug = product?.slug || product?.seo_slug;
    if (!slug) return;

    const fullProduct = await getProductBySlug(slug);

    const firstVariant =
      fullProduct?.variants?.find((v) => Number(v.stock_qty) > 0) ||
      fullProduct?.variants?.[0] ||
      null;

    if (!firstVariant) {
      goToRecommendedProduct(product);
      return;
    }

    const basePrice = Number(fullProduct.base_price || product.price || 0);
    const salePercent = Number(
      fullProduct.sale_percent || product.discount || 0
    );
    const isOnSale = Boolean(fullProduct.on_sale || salePercent > 0);

    const productPrice = isOnSale
      ? Math.round(basePrice * (1 - salePercent / 100))
      : basePrice;

    const itemPrice = productPrice + Number(firstVariant.extra_price || 0);

    const media = resolveRecommendedMedia(product);
    const mediaIsVideo = isRecommendedVideo(product);

    await addToCart({
      product_id: fullProduct.id,
      variant_id: firstVariant.id,
      bxgyDetails: fullProduct.bxgy_details || fullProduct.bxgyDetails || [],

      public_id: fullProduct.public_id,
      nameEN: fullProduct.name_en,
      nameAR: fullProduct.name_ar,
      price: itemPrice,
      image: media,
      size: firstVariant.size,
      color: firstVariant.color_name,
      color_hex: firstVariant.color_hex || "#000000",
    });

    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "add_to_cart",
        entity_type: "product",
        entity_id: fullProduct.id,
      }),
    }).catch(() => {});

    let shippingSettings = null;

    try {
      shippingSettings = await apiRequest("/shipping-settings");
    } catch {}

    const newCartCount =
      cartItems.reduce((sum, item) => sum + Number(item.qty || 0), 0) + 1;

    const newSubtotal = Number(subtotal || 0) + Number(itemPrice || 0);

    const freeEnabled = !!shippingSettings?.free_enabled;
    const freeThreshold = Number(shippingSettings?.free_threshold || 0);
    const missingForFree = Math.max(0, freeThreshold - newSubtotal);

    setRecommendedAddedModal({
      image: media,
      isVideo: mediaIsVideo,
      count: newCartCount,
      subtotal: newSubtotal,
      total: newSubtotal,
      freeEnabled,
      freeThreshold,
      missingForFree,
    });
  } catch (err) {
    console.error(err);
    goToRecommendedProduct(product);
  } finally {
    setRecommendedAddingId(null);
  }
};

const safeCloseCart = () => {
  sessionStorage.removeItem("velle-direct-shared-cart");

  navigate(`/?lang=${lang}`, { replace: true });
};

const goToCheckout = () => {
  if (disableCheckout) return;

  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "enter_checkout",
      entity_type: "cart",
    }),
  }).catch(() => {});

  const params = new URLSearchParams();

  params.set("lang", lang);

  if (cartShareToken) {
    params.set("cart_token", cartShareToken);
  }

  navigate(`/checkout?${params.toString()}`, {
    replace: true,
    state: {
      fromCartPage: true,
      couponCode: cartCoupon?.code || null,
    },
  });
};

const CartMobileSkeleton = () => (
  <div className="md:hidden w-full min-h-screen bg-white pb-[96px]">
    <TopBanner />
    <MainHeader />

    <div dir={lang === "AR" ? "rtl" : "ltr"}>
      <section className="bg-[#f3f3f3] px-5 py-8 text-center">
        <div className="mx-auto h-[34px] w-[230px] bg-gray-200 animate-pulse rounded" />
      </section>

      <section className="px-5 pt-4 pb-6 bg-white">
        <div className="h-[22px] w-[95px] bg-gray-200 animate-pulse rounded mb-5" />

        <div className="space-y-8">
          {[1, 2].map((i) => (
            <div key={i} className="grid grid-cols-[38%_1fr] gap-5 border-b border-[#e5e5e5] pb-7">
              <div className="w-full aspect-[3/4] bg-gray-200 animate-pulse" />

              <div className="pt-1">
                <div className="h-5 w-full bg-gray-200 animate-pulse rounded" />
                <div className="mt-3 h-4 w-[65%] bg-gray-200 animate-pulse rounded" />
                <div className="mt-3 h-4 w-[45%] bg-gray-200 animate-pulse rounded" />
                <div className="mt-6 h-6 w-[90px] bg-gray-200 animate-pulse rounded" />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white px-5 py-9">
        <div className="h-[28px] w-[120px] bg-gray-200 animate-pulse rounded mb-8" />
        <div className="space-y-4">
          <div className="h-5 w-full bg-gray-200 animate-pulse rounded" />
          <div className="h-5 w-full bg-gray-200 animate-pulse rounded" />
          <div className="h-[58px] w-full bg-gray-200 animate-pulse rounded mt-8" />
        </div>
      </section>
    </div>
  </div>
);

const CartDesktopSkeleton = () => (
  <div
    className="hidden md:block w-full min-h-screen bg-white"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <TopBanner />
    <MainHeader />

    <section className="border-t border-black bg-[#f4f4f4] mt-[156px]">
      <div className="max-w-[1180px] mx-auto px-6 py-12 text-center">
        <div className="mx-auto h-[28px] w-[250px] bg-gray-200 animate-pulse rounded" />
      </div>
    </section>

    <section className="bg-[#f4f4f4] pb-16">
      <div className="max-w-[1350px] mx-auto px-6 pt-8">
        <div className="grid grid-cols-[minmax(0,1fr)_330px] gap-8 items-start">
          <div className="bg-white px-6 py-6">
            <div className="h-[22px] w-[110px] bg-gray-200 animate-pulse rounded mb-6" />

            <div className="space-y-8">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="grid grid-cols-[118px_minmax(0,1fr)_120px] gap-6 pb-8 border-b border-[#ededed]"
                >
                  <div className="w-[118px] h-[158px] bg-gray-200 animate-pulse" />

                  <div className="pt-1">
                    <div className="h-5 w-[80%] bg-gray-200 animate-pulse rounded" />
                    <div className="mt-3 h-4 w-[40%] bg-gray-200 animate-pulse rounded" />
                    <div className="mt-3 h-4 w-[30%] bg-gray-200 animate-pulse rounded" />
                    <div className="mt-16 h-5 w-[170px] bg-gray-200 animate-pulse rounded" />
                  </div>

                  <div className="pt-4">
                    <div className="h-5 w-[80px] bg-gray-200 animate-pulse rounded ml-auto" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="md:sticky md:top-[160px] bg-white px-6 py-6">
            <div className="h-[24px] w-[120px] bg-gray-200 animate-pulse rounded mb-6" />
            <div className="space-y-4">
              <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
              <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
              <div className="h-px w-full bg-gray-200 my-5" />
              <div className="h-5 w-full bg-gray-200 animate-pulse rounded" />
              <div className="h-[48px] w-full bg-gray-200 animate-pulse rounded mt-6" />
            </div>
          </aside>
        </div>
      </div>
    </section>
  </div>
);

if (cartLoading) {
  return (
    <>
      <CartMobileSkeleton />
      <CartDesktopSkeleton />
    </>
  );
}

  return (
  <>
    {/* MOBILE CART PAGE */}
    <div className="md:hidden w-full min-h-screen bg-white pb-[96px]">
      <TopBanner />
      <MainHeader />

      <div dir={lang === "AR" ? "rtl" : "ltr"}>
        {/* TITLE */}
        <section className="relative bg-[#f3f3f3] px-5 py-8 text-center">
  <button
    type="button"
    onClick={safeCloseCart}
    className={`
      absolute
      top-1/2
      -translate-y-1/2
      text-[34px]
      leading-none
      text-black/60
      hover:text-black
      transition
      ${lang === "AR" ? "right-5" : "left-5"}
    `}
    aria-label="Close cart"
  >
    ×
  </button>

  <h2 className="text-[30px] leading-none font-semibold text-black">
    {lang === "AR" ? "سلة التسوق" : "Your shopping bag"}
  </h2>
</section>

        {/* EMPTY STATE */}
        {isEmpty && (
  <>
    <div className="px-5 py-16 text-center">
      <p className="text-[18px] text-black">
        {t.empty}
      </p>

      <button
        onClick={() => navigate("/")}
        className="
          mt-8
          w-full h-[58px]
          bg-[#ff9f00]
          text-white
          text-[20px]
          font-normal
          rounded-[4px]
          active:scale-[0.99]
        "
      >
        {t.continue}
      </button>
    </div>

    <Footer />
  </>
)}

        {!isEmpty && (
          <>
            {/* PRODUCTS */}
            <section className="px-5 pt-4 pb-6 bg-white">
              <h2 className="text-[19px] font-medium leading-none font-normal text-black mb-4">
                {lang === "AR" ? "المنتجات" : "Products"}
              </h2>

              <div className="space-y-8">
                {[...normalItems, ...discountedItems, ...freeItems].map((item) => {
                  const itemName = getCartItemName(item);
                  const itemPrice = getCartItemFinalPrice(item);
                  const originalPrice = getCartItemBasePrice(item);
                  const hasItemDiscount =
                    item.isBxgyDiscounted ||
                    Number(item.bxgyDiscountValue || 0) > 0;

                  return (
                    <div
  key={item.id}
  className={`
    relative
    border-b border-[#e5e5e5]
    pb-7 last:border-b-0
    ${isItemInvalid(item) ? "border-2 border-red-600 p-3" : ""}
  `}
>
  {isItemInvalid(item) && (
    <div className="absolute top-0 left-0 right-0 bg-red-600 text-white text-[13px] font-bold text-center py-1 z-10">
      {(() => {
  const stock = getSafeStockQty(item);

  return stock !== null && stock <= 0
    ? lang === "AR"
      ? "غير متوفر"
      : "OUT OF STOCK"
    : lang === "AR"
    ? "الكمية أكبر من المخزون"
    : "QUANTITY EXCEEDS STOCK";
})()}
    </div>
  )}
                      <div className="grid grid-cols-[38%_1fr] gap-5 items-start">
                        <button
                          type="button"
                          onClick={() => item.slug && navigate(`/product/${encodeURIComponent(item.slug)}`)}
                          className="block bg-[#f5f5f5] overflow-hidden"
                        >
                          {String(item.image || "").match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                            <video
                              src={item.image}
                              muted
                              playsInline
                              preload="metadata"
                              className="w-full aspect-[3/4] object-cover block"
                            />
                          ) : (
                            <img
                              src={item.image || "/images/placeholder-product.jpg"}
                              alt={itemName}
                              className="w-full aspect-[3/4] object-cover block"
                            />
                          )}
                        </button>

                        <div className="min-w-0 pt-1">
                          <h3 className="text-[18px] leading-[1.45] font-normal text-black">
                            {itemName}
                          </h3>

                          {item.size && (
  <p className="mt-3 text-[17px] leading-none text-[#8a8a8a]">
    {lang === "AR" ? "المقاس" : "Size"}: {item.size}
  </p>
)}

{item.color && (
  <div className="mt-3 flex items-center gap-2 text-[17px] leading-none text-[#8a8a8a]">
    <span>{lang === "AR" ? "اللون" : "Color"}:</span>
    <span
      className="inline-block w-[14px] h-[14px] rounded-full border border-gray-300"
      style={{ backgroundColor: getCartItemColorHex(item) }}
      title={item.color}
    />
  </div>
)}

<p className="mt-3 text-[17px] leading-none text-[#8a8a8a]">
  {lang === "AR" ? "الكمية" : "Quantity"}: {item.qty}
</p>
{item.isBxgyFree && (
  <p className="mt-2 text-[13px] text-green-700 font-semibold">
    {lang === "AR" ? "هدية مجانية" : "Free gift"}
  </p>
)}

{item.isBxgyDiscounted && (
  <p className="mt-2 text-[13px] text-green-700 font-semibold">
    {lang === "AR"
      ? `خصم ${item.bxgyDiscountValue}%`
      : `${item.bxgyDiscountValue}% BXGY discount`}
  </p>
)}

                          {pricesSalesVisible && itemHasAnyPriceDiscount(item) && (
  <span
    className="
      mt-4
      inline-flex
      h-[28px]
      min-w-[60px]
      items-center
      justify-center
      rounded-[6px]
      bg-[#c40012]
      px-3
      text-[14px]
      text-white
      font-normal
    "
  >
    -{getItemDisplayDiscountPercent(item)}%
  </span>
)}

<div className="mt-3">
 <p className="text-[22px] leading-none font-semibold text-black">
  {!pricesSalesVisible
    ? t.priceHidden
    : item.isBxgyFree
    ? lang === "AR"
      ? "مجاني"
      : "FREE"
    : formatPrice(getCartItemFinalTotal(item))}
</p>

  {pricesSalesVisible && itemHasAnyPriceDiscount(item) &&
  getCartItemCompareTotal(item) > getCartItemFinalTotal(item) && (
    <p className="mt-2 text-[16px] text-black line-through">
      {formatPrice(getCartItemCompareTotal(item))}
    </p>
  )}

  {pricesSalesVisible && !item.isBxgyFree && Number(item.qty || 1) > 1 && (
  <p className="mt-2 text-[16px] text-black">
    {lang === "AR" ? "لكل قطعة" : "Per piece"}:
    {formatPrice(Number(item.price || 0))}
  </p>
)}
</div>
                        </div>
                      </div>

                      <div className="mt-8 flex items-center gap-8">
  <button
    type="button"
    onClick={() => removeFromCart(item.id)}
    className="inline-flex items-center gap-2 text-[17px] text-black"
  >
    <TrashIcon />
    {lang === "AR" ? "إزالة" : "Remove"}
  </button>

  <button
    type="button"
    onClick={() =>
      setEditItemId((prev) => (prev === item.id ? null : item.id))
    }
    className="inline-flex items-center gap-2 text-[17px] text-black"
  >
    <span className="text-[18px]">✎</span>
    {lang === "AR" ? "تغيير الكمية" : "Change quantity"}
  </button>
</div>

                      {editItemId === item.id && (
  <div className="mt-5 border border-[#d9d9d9] bg-white px-4 py-4">
    {editLoading ? (
      <p className="text-[15px] text-gray-500">
        {lang === "AR" ? "جاري تحميل الخيارات..." : "Loading options..."}
      </p>
    ) : (
      <>
        {/* COLORS */}
        {getEditColors().length > 0 && (
          <div>
            <p className="text-[15px] font-semibold uppercase text-black">
              {lang === "AR" ? "اللون" : "Color"}
            </p>

            <div className="mt-2 flex gap-2 flex-wrap">
              {getEditColors().map((color) => {
                const active = editColor === color.name;
                const imageUrl = getEditColorImage(color.name);

                return (
                  <button
                    key={color.name}
                    type="button"
                    onClick={() => {
                      const firstVariant =
                        editProduct?.variants?.find(
                          (v) =>
                            v.color_name === color.name &&
                            Number(v.stock_qty || 0) > 0
                        ) || null;

                      setEditColor(color.name);
                      setEditVariant(firstVariant);
                      setEditQty(1);
                      setEditError("");
                    }}
                    className={`
                      w-[54px] h-[74px]
                      overflow-hidden
                      border-2
                      bg-[#f7f7f7]
                      ${active ? "border-black" : "border-transparent"}
                    `}
                    title={color.name}
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={color.name}
                        className="w-full h-full object-cover block"
                      />
                    ) : (
                      <span
                        className="block w-full h-full"
                        style={{ backgroundColor: color.code }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* SIZES */}
        {getEditSizes().length > 0 && (
          <div className="mt-5">
            <p className="text-[15px] font-semibold uppercase text-black">
              {lang === "AR" ? "المقاس" : "Size"}
            </p>

            <div className="mt-2 flex gap-2 flex-wrap">
              {getEditSizes().map((variant) => {
                const active =
                  Number(editVariant?.id) === Number(variant.id);
                const disabled = Number(variant.stock_qty || 0) <= 0;

                return (
                  <button
                    key={variant.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      setEditVariant(variant);
                      setEditQty((q) =>
                        Math.min(
                          Math.max(1, Number(q || 1)),
                          Number(variant.stock_qty || 1)
                        )
                      );
                      setEditError("");
                    }}
                    className={`
                      min-w-[43px] h-[43px]
                      border-2 border-black
                      bg-white
                      p-[4px]
                      text-[15px]
                      ${disabled ? "opacity-40 line-through" : ""}
                    `}
                  >
                    <span
                      className={`
                        w-full h-full
                        flex items-center justify-center
                        ${active ? "bg-black text-white" : "bg-white text-black"}
                      `}
                    >
                      {variant.size}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* QUANTITY */}
        <div className="mt-5">
          <p className="text-[15px] font-semibold uppercase text-black">
            {lang === "AR" ? "الكمية" : "Quantity"}
          </p>

          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              disabled={editQty <= 1}
              onClick={() => setEditQty((q) => Math.max(1, Number(q || 1) - 1))}
              className="
                w-[44px] h-[38px]
                border-2 border-black
                text-[24px]
                disabled:opacity-40
              "
            >
              -
            </button>

            <input
              type="number"
              min="1"
              max={Number(editVariant?.stock_qty || 1)}
              value={editQty}
              onChange={(e) => {
                const max = Number(editVariant?.stock_qty || 1);
                const value = Math.min(
                  Math.max(1, Number(e.target.value || 1)),
                  max
                );
                setEditQty(value);
              }}
              className="
                w-[70px] h-[38px]
                border-2 border-black
                text-center
                text-[18px]
                outline-none
              "
            />

            <button
              type="button"
              disabled={
                !editVariant ||
                editQty >= Number(editVariant.stock_qty || 0)
              }
              onClick={() => {
                const max = Number(editVariant?.stock_qty || 1);
                setEditQty((q) => Math.min(max, Number(q || 1) + 1));
              }}
              className="
                w-[44px] h-[38px]
                border-2 border-black
                text-[24px]
                disabled:opacity-40
              "
            >
              +
            </button>
          </div>

          {editVariant && (
            <p className="mt-2 text-[13px] text-gray-500">
              {lang === "AR" ? "المتوفر" : "Available"}:{" "}
              {Number(editVariant.stock_qty || 0)}
            </p>
          )}
        </div>

        {editError && (
          <p className="mt-3 text-[13px] text-red-600 font-semibold">
            {editError}
          </p>
        )}

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setEditItemId(null)}
            className="h-[44px] border border-black text-black text-[15px]"
          >
            {lang === "AR" ? "إلغاء" : "Cancel"}
          </button>

          <button
            type="button"
            onClick={() => applyCartItemEdit(item)}
            className="h-[44px] bg-black text-white text-[15px]"
          >
            {lang === "AR" ? "حفظ" : "Save"}
          </button>
        </div>
      </>
    )}
  </div>
)}
                    </div>
                  );
                })}
              </div>

<CartBxgySuggestions />

              <div className="mt-5 bg-[#f3f3f3] px-5 py-4 flex items-start gap-4">
                <span className="text-[18px] font-bold">ℹ</span>
                <p className="text-[14px] leading-[1.45] text-black">
                  {lang === "AR"
                    ? "لا تؤجل عملية الشراء، إضافة المنتجات إلى السلة لا تعني أنها محجوزة لك."
                    : "Don't wait with your purchase, adding products to your bag does not mean that they are reserved."}
                </p>
              </div>
            </section>

            {/* RECOMMENDED PRODUCTS */}
            {recommendedProducts.length > 0 && (
              <section className="bg-[#f3f3f3] pt-9 pb-10">
                <h2 className="px-5 text-center text-[28px] leading-[1.1] font-semibold text-black">
                  {lang === "AR"
                    ? "قد يعجبك أيضاً"
                    : "You may also be interested"}
                </h2>

                <div className="relative mt-5">
                  <button
                    type="button"
                    onClick={() => scrollRecommended("prev")}
                    className="
                      absolute left-0 top-[37%] z-20
                      w-[34px] h-[58px]
                      bg-white/90
                      text-black
                      text-[30px]
                      flex items-center justify-center
                    "
                  >
                    ‹
                  </button>

                  <div
                    ref={recommendedRailRef}
                    className="
                      flex gap-3
                      overflow-x-auto
                      no-scrollbar
                      scroll-smooth
                      px-0
                    "
                  >
                    {recommendedProducts.map((product) => {
                      const hasSale = Number(product.discount || 0) > 0;
                      const final = getRecommendedFinalPrice(product);
                      const original = Number(product.price || 0);
                      const media = resolveRecommendedMedia(product);
                      const mediaIsVideo = isRecommendedVideo(product);

                      return (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => goToRecommendedProduct(product)}
                          className="
                            shrink-0
                            w-[calc((100vw-12px)/2)]
                            bg-white
                            text-left
                          "
                        >
                          <div className="relative bg-[#f5f5f5] overflow-hidden">
                            {hasSale && (
                              <span
                                className="
                                  absolute top-4 right-0 z-10
                                  bg-[#c40012]
                                  text-white
                                  px-2 py-2
                                  text-[15px]
                                "
                              >
                                -{Number(product.discount)}%
                              </span>
                            )}

                            {mediaIsVideo ? (
                              <video
                                src={media}
                                muted
                                loop
                                autoPlay
                                playsInline
                                preload="metadata"
                                className="w-full h-[270px] object-cover block"
                              />
                            ) : (
                              <img
                                src={media}
                                alt={getRecommendedName(product)}
                                className="w-full h-[270px] object-cover block"
                                loading="lazy"
                              />
                            )}
                          </div>

                          <div className="px-4 pt-3 pb-5">
                            <h3 className="text-[18px] leading-[1.22] text-[#777] font-normal">
                              {getRecommendedName(product)}
                            </h3>

                            <div className="mt-8 flex items-center gap-2">
                              <span className="text-[17px] text-black">
                                {formatPrice(final)}
                              </span>

                              {hasSale && (
                                <span className="text-[16px] text-black line-through">
                                  {formatPrice(original)}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => scrollRecommended("next")}
                    className="
                      absolute right-0 top-[37%] z-20
                      w-[34px] h-[58px]
                      bg-white/90
                      text-black
                      text-[30px]
                      flex items-center justify-center
                    "
                  >
                    ›
                  </button>
                </div>
              </section>
            )}

            {/* SUMMARY */}
            <section className="bg-white px-5 py-9">
              <h2 className="text-[27px] leading-none font-normal text-black mb-8">
                {lang === "AR" ? "الملخص" : "Summary"}
              </h2>

              <div className="space-y-4 text-[20px] text-black">
  <div className="flex items-center justify-between gap-4">
    <span>{lang === "AR" ? "المجموع" : "Subtotal"}</span>
    <span>{pricesSalesVisible ? formatPrice(subtotal) : t.priceHidden}</span>
  </div>

  {effectiveOrderDiscountAmount > 0 && (
    <div className="flex items-center justify-between gap-4 text-green-700">
      <span>{lang === "AR" ? "خصم الطلب" : "Order discount"}</span>
      <span>{pricesSalesVisible ? `-${formatPrice(effectiveOrderDiscountAmount)}` : t.priceHidden}</span>
    </div>
  )}

  {cartCoupon && (
    <div className="flex items-center justify-between gap-4 text-green-700">
      <span>{lang === "AR" ? "كوبون" : "Coupon"}</span>
      <span>{pricesSalesVisible ? `-${formatPrice(couponDiscount)}` : t.priceHidden}</span>
    </div>
  )}

  <div className="flex items-center justify-between gap-4">
    <span>{lang === "AR" ? "توصيل" : "Delivery"}</span>
    <span>
      {deliveryFee === 0
        ? isFreeDeliveryFromDiscount
          ? lang === "AR"
            ? "مجاني (خصم)"
            : "Free (discount)"
          : lang === "AR"
          ? "مجاني"
          : "Free"
        : pricesSalesVisible ? formatPrice(deliveryFee) : t.priceHidden}
    </span>
  </div>

  {freeShippingDiscount && (
    <div
      className={`
        border px-3 py-3 text-[13px] leading-[1.45]
        ${
          isFreeDeliveryFromDiscount
            ? "border-green-200 bg-green-50 text-green-800"
            : "border-orange-200 bg-orange-50 text-orange-800"
        }
      `}
    >
      <div className="font-semibold">
        {freeShippingInfo.label}
      </div>

      {freeShippingInfo.endLabel && (
        <div className="mt-1 opacity-80">
          {lang === "AR"
            ? `ينتهي في ${freeShippingInfo.endLabel}`
            : `Ends on ${freeShippingInfo.endLabel}`}
        </div>
      )}
    </div>
  )}
</div>

              {hasActiveCoupons && (
  <div className="mt-6 border-t border-black">
    <button
      type="button"
      onClick={() => setCouponOpen((prev) => !prev)}
      className="
        w-full
        h-[54px]
        flex items-center gap-3
        text-[22px]
        text-black
      "
    >
      <ChevronDownIcon open={couponOpen} />
      {lang === "AR" ? "كود الخصم" : "Discount code"}
    </button>

    {couponOpen && (
      <div className="mb-5">
        <div className="grid grid-cols-[1fr_48%] h-[45px]">
          <input
            value={couponInput}
            onChange={(e) => {
              setCouponInput(e.target.value);
              setCouponError(null);
            }}
            className="
              border border-[#cfcfcf]
              px-3
              text-[18px]
              outline-none
            "
          />

          <button
            type="button"
            onClick={applyMobileCoupon}
            disabled={couponLoading}
            className="
              bg-black
              text-white
              text-[21px]
              disabled:opacity-60
            "
          >
            {couponLoading ? "..." : lang === "AR" ? "استخدام" : "Use"}
          </button>
        </div>

        {cartCoupon && (
          <div className="mt-3 flex items-center justify-between text-[15px] text-green-700">
            <span>
              {lang === "AR" ? "تم تطبيق الكوبون" : "Coupon applied"}:{" "}
              {cartCoupon.code}
            </span>

            <button
              type="button"
              onClick={clearMobileCoupon}
              className="underline text-black"
            >
              {lang === "AR" ? "إزالة" : "Remove"}
            </button>
          </div>
        )}

        {couponError && (
          <p className="mt-2 text-[14px] text-red-600">
            {couponError === "expired"
              ? lang === "AR"
                ? "انتهت صلاحية الكوبون"
                : "Coupon expired"
              : lang === "AR"
              ? "رمز غير صالح"
              : "Unavailable code"}
          </p>
        )}
      </div>
    )}
  </div>
)}

              <div className="border-t border-black pt-7">

                <div className="flex items-center justify-between text-[21px] font-bold text-black">
                  <span>{lang === "AR" ? "الإجمالي" : "Total"}</span>
                  <span>{pricesSalesVisible ? formatPrice(cartSummaryTotal) : t.priceHidden}</span>
                </div>
              </div>
            </section>

            {/* HELP */}
            <section className="bg-[#f7f7f7] px-0 py-10">
              <div className="bg-white px-5 py-8">
                <h2 className="text-[23px] leading-none font-normal text-black">
                  {lang === "AR" ? "كيف يمكننا مساعدتك؟" : "Can we help you somehow?"}
                </h2>

                <div className="mt-5 h-px bg-[#cfcfcf]" />

                <div className="mt-7 flex items-start gap-5">
                  <PhoneIcon />

                  <div>
                    <p className="text-[18px] text-[#777]">
                      {lang === "AR" ? "اتصل بنا:" : "Call us:"}
                    </p>
                    <p className="mt-2 text-[24px] font-semibold text-black">
                      81 446 356
                    </p>

                    <p className="mt-2 text-[15px] leading-[1.7] text-[#777]">
                      {lang === "AR"
                        ? "خدمة العملاء متاحة من الإثنين إلى الجمعة من 8:00 صباحاً حتى 4:00 مساءً"
                        : "Customer service office is open from Monday to Friday from 8:00 a.m. to 4:00 p.m"}
                    </p>
                  </div>
                </div>
{!pricesSalesVisible && (
  <div className="mb-3 border border-orange-200 bg-orange-50 text-orange-700 text-sm px-4 py-3 rounded-md">
    {t.pricesHiddenCheckout}
  </div>
)}
                <button
  ref={helpCheckoutRef}
  type="button"
  disabled={disableCheckout}
                  onClick={goToCheckout}
                  className={`
                    mt-9
                    w-full h-[58px]
                    rounded-[5px]
                    text-white
                    text-[21px]
                    font-normal
                    ${disableCheckout ? "bg-gray-400" : "bg-[#ff9f00]"}
                  `}
                >
                  {lang === "AR" ? "الانتقال للدفع »" : "Go to checkout  »"}
                </button>

                <div className="mt-8 space-y-5 text-[18px] text-black">
                  <div className="flex items-center gap-5">
                    <TruckIcon />
                    <span>
                      {shipping.free_enabled && Number(shipping.free_threshold) > 0
                        ? `${lang === "AR" ? "توصيل مجاني من" : "Free delivery from"} ${formatPrice(shipping.free_threshold)}`
                        : lang === "AR"
                        ? "توصيل متوفر"
                        : "Delivery available"}
                    </span>
                  </div>

                  <div className="flex items-center gap-5">
                    <ShieldIcon />
                    <span>{lang === "AR" ? "تسوق آمن عبر الإنترنت" : "Safe online shopping"}</span>
                  </div>
                </div>
              </div>
            </section>
            <Footer />
          </>
        )}
{disableCheckout && !isEmpty && (
  <div className="fixed left-0 right-0 bottom-[82px] z-[9998] px-5">
    <div className="bg-red-600 text-white text-center text-[14px] font-semibold px-4 py-3 rounded-md shadow-lg">
      {lang === "AR"
        ? "يرجى تعديل المنتجات المحددة بالأحمر قبل الدفع"
        : "Please fix the red highlighted products before checkout"}
    </div>
  </div>
)}
        {/* FIXED CHECKOUT BUTTON */}
        {!isEmpty && showFixedCheckout && (
  <div className="fixed left-0 right-0 bottom-0 z-[9998] bg-white/95 px-5 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)]">
            {!pricesSalesVisible && (
      <div className="mb-2 border border-orange-200 bg-orange-50 text-orange-700 text-[13px] px-3 py-2 rounded-md text-center">
        {t.pricesHiddenCheckout}
      </div>
    )}
            <button
              type="button"
              disabled={disableCheckout}
              onClick={goToCheckout}
              className={`
                w-full h-[58px]
                rounded-[5px]
                text-white
                text-[21px]
                font-normal
                ${disableCheckout ? "bg-gray-400" : "bg-[#ff9f00]"}
              `}
            >
              {lang === "AR" ? "الانتقال للدفع »" : "Go to checkout  »"}
            </button>
          </div>
        )}
      </div>
    </div>

    {/* DESKTOP CART PAGE */}
<div
  className="hidden md:block w-full min-h-screen bg-white"
  dir={lang === "AR" ? "rtl" : "ltr"}
>
  <TopBanner />
  <MainHeader />

  {/* EMPTY DESKTOP STATE */}
  {isEmpty ? (
    <>
      <section className="bg-[#f4f4f4] border-t border-[#d8d8d8]">
  <div className="relative max-w-[1180px] mx-auto px-6 py-16 text-center">
    <button
      type="button"
      onClick={safeCloseCart}
      className={`
        absolute
        top-8
        text-[34px]
        leading-none
        text-black/60
        hover:text-black
        transition
        ${lang === "AR" ? "right-6" : "left-6"}
      `}
      aria-label="Close cart"
    >
      ×
    </button>
          <h1 className="text-[26px] font-normal text-[#333]">
            {lang === "AR" ? "سلة التسوق فارغة" : "Your shopping bag is empty"}
          </h1>

          <p className="mt-5 text-[15px] text-[#777]">
            {t.empty}
          </p>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="
              mt-8
              min-w-[250px]
              h-[48px]
              bg-[#e99a22]
              text-white
              text-[15px]
              font-normal
              hover:opacity-90
              transition
            "
          >
            {t.continue}
          </button>
        </div>
      </section>

      <Footer />
    </>
  ) : (
    <>
      {/* PAGE TITLE */}
      <section className="border-t border-black bg-[#f4f4f4] mt-[156px]">
  <div className="relative max-w-[1180px] mx-auto px-6 py-12 text-center">
    <button
      type="button"
      onClick={safeCloseCart}
      className={`
        absolute
        top-1/2
        -translate-y-1/2
        text-[34px]
        leading-none
        text-black/60
        hover:text-black
        transition
        ${lang === "AR" ? "right-6" : "left-6"}
      `}
      aria-label="Close cart"
    >
      ×
    </button>

    <h1 className="text-[26px] leading-none font-bold text-[#333]">
      {lang === "AR" ? "سلة التسوق" : "Your shopping bag"}
    </h1>
  </div>
</section>

      {/* CART CONTENT */}
      <section className="bg-[#f4f4f4] pb-16">
  <div className="max-w-[1350px] mx-auto px-6 pt-8">
    <div className="grid grid-cols-[minmax(0,1fr)_330px] gap-8 items-start">
      {/* LEFT COLUMN */}
<div className="min-w-0">
        <div className="space-y-8">
          {/* PRODUCTS CARD */}
          <div className="bg-white px-6 py-6">
            <h2 className="text-[18px] font-normal text-[#333] mb-5">
              {lang === "AR" ? "المنتجات" : "Products"}
            </h2>

            <div className="space-y-10">
              {cartDisplayItems.map((item) => {
                const itemName = getCartItemName(item);
                const hasSale = itemHasAnyPriceDiscount(item);
                const stock = getSafeStockQty(item);
                const invalid = isItemInvalid(item);

                return (
                  <div
                    key={item.id}
                    className={`
                      relative
                      grid grid-cols-[118px_minmax(0,1fr)_120px]
                      gap-6
                      pb-8
                      border-b border-[#ededed]
                      ${invalid ? "border-2 border-red-600 p-3" : ""}
                    `}
                  >
                    {invalid && (
                      <div className="absolute top-0 left-0 right-0 bg-red-600 text-white text-[12px] font-semibold text-center py-1 z-10">
                        {stock !== null && stock <= 0
                          ? lang === "AR"
                            ? "غير متوفر"
                            : "OUT OF STOCK"
                          : lang === "AR"
                          ? "الكمية أكبر من المخزون"
                          : "QUANTITY EXCEEDS STOCK"}
                      </div>
                    )}

                    {/* IMAGE */}
                    <button
                      type="button"
                      onClick={() =>
                        item.slug &&
                        navigate(`/product/${encodeURIComponent(item.slug)}`)
                      }
                      className="block bg-[#f6f6f6] overflow-hidden"
                    >
                      {String(item.image || "").match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                        <video
                          src={item.image}
                          muted
                          playsInline
                          preload="metadata"
                          className="w-[118px] h-[158px] object-cover block"
                        />
                      ) : (
                        <img
                          src={item.image || "/images/placeholder-product.jpg"}
                          alt={itemName}
                          className="w-[118px] h-[158px] object-cover block"
                        />
                      )}
                    </button>

                    {/* DETAILS */}
                    <div className="pt-1 min-w-0">
                      <h3 className="text-[16px] leading-[1.45] font-normal max-w-[430px]">
                        {itemName}
                      </h3>

                      {item.size && (
  <p className="mt-2 text-[13px] text-[#8a8a8a]">
    {lang === "AR" ? "المقاس" : "Size"}: {item.size}
  </p>
)}

{item.color && (
  <div className="mt-1 flex items-center gap-2 text-[13px] text-[#8a8a8a]">
    <span>{lang === "AR" ? "اللون" : "Color"}:</span>
    <span
      className="inline-block w-[11px] h-[11px] rounded-full border border-gray-300"
      style={{ backgroundColor: getCartItemColorHex(item) }}
      title={item.color}
    />
  </div>
)}

<p className="mt-1 text-[13px] text-[#8a8a8a]">
  {lang === "AR" ? "الكمية" : "Quantity"}: {item.qty}
</p>
{item.isBxgyFree && (
  <p className="mt-2 text-[12px] text-green-700 font-semibold">
    {lang === "AR" ? "هدية مجانية" : "Free gift"}
  </p>
)}

{item.isBxgyDiscounted && (
  <p className="mt-2 text-[12px] text-green-700 font-semibold">
    {lang === "AR"
      ? `خصم ${item.bxgyDiscountValue}%`
      : `${item.bxgyDiscountValue}% BXGY discount`}
  </p>
)}
                      <div className="mt-16 flex items-center gap-8">
                        <button
                          type="button"
                          onClick={() => removeFromCart(item.id)}
                          className="inline-flex items-center gap-2 text-[14px] hover:text-black transition"
                        >
                          <TrashIcon />
                          {lang === "AR" ? "إزالة" : "Remove"}
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            setEditItemId((prev) =>
                              prev === item.id ? null : item.id
                            )
                          }
                          className="inline-flex items-center gap-2 text-[14px] hover:text-black transition"
                        >
                          <span className="text-[16px]">✎</span>
                          {lang === "AR" ? "تغيير الكمية" : "Change quantity"}
                        </button>
                      </div>

                      {editItemId === item.id && (
                        <div className="mt-5 border border-[#d9d9d9] bg-white px-4 py-4 max-w-[520px]">
                          {editLoading ? (
                            <p className="text-[14px] text-gray-500">
                              {lang === "AR"
                                ? "جاري تحميل الخيارات..."
                                : "Loading options..."}
                            </p>
                          ) : (
                            <>
                              {getEditColors().length > 0 && (
                                <div>
                                  <p className="text-[13px] font-semibold uppercase text-black">
                                    {lang === "AR" ? "اللون" : "Color"}
                                  </p>

                                  <div className="mt-2 flex gap-2 flex-wrap">
                                    {getEditColors().map((color) => {
                                      const active = editColor === color.name;
                                      const imageUrl = getEditColorImage(color.name);

                                      return (
                                        <button
                                          key={color.name}
                                          type="button"
                                          onClick={() => {
                                            const firstVariant =
                                              editProduct?.variants?.find(
                                                (v) =>
                                                  v.color_name === color.name &&
                                                  Number(v.stock_qty || 0) > 0
                                              ) || null;

                                            setEditColor(color.name);
                                            setEditVariant(firstVariant);
                                            setEditQty(1);
                                            setEditError("");
                                          }}
                                          className={`
                                            w-[48px] h-[64px]
                                            overflow-hidden
                                            border-2
                                            bg-[#f7f7f7]
                                            ${active ? "border-black" : "border-transparent"}
                                          `}
                                          title={color.name}
                                        >
                                          {imageUrl ? (
                                            <img
                                              src={imageUrl}
                                              alt={color.name}
                                              className="w-full h-full object-cover block"
                                            />
                                          ) : (
                                            <span
                                              className="block w-full h-full"
                                              style={{ backgroundColor: color.code }}
                                            />
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {getEditSizes().length > 0 && (
                                <div className="mt-4">
                                  <p className="text-[13px] font-semibold uppercase text-black">
                                    {lang === "AR" ? "المقاس" : "Size"}
                                  </p>

                                  <div className="mt-2 flex gap-2 flex-wrap">
                                    {getEditSizes().map((variant) => {
                                      const active =
                                        Number(editVariant?.id) === Number(variant.id);
                                      const disabled =
                                        Number(variant.stock_qty || 0) <= 0;

                                      return (
                                        <button
                                          key={variant.id}
                                          type="button"
                                          disabled={disabled}
                                          onClick={() => {
                                            setEditVariant(variant);
                                            setEditQty((q) =>
                                              Math.min(
                                                Math.max(1, Number(q || 1)),
                                                Number(variant.stock_qty || 1)
                                              )
                                            );
                                            setEditError("");
                                          }}
                                          className={`
                                            min-w-[38px] h-[38px]
                                            border border-black
                                            bg-white
                                            text-[13px]
                                            ${disabled ? "opacity-40 line-through" : ""}
                                          `}
                                        >
                                          <span
                                            className={`
                                              w-full h-full
                                              flex items-center justify-center px-2
                                              ${active ? "bg-black text-white" : "bg-white text-black"}
                                            `}
                                          >
                                            {variant.size}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              <div className="mt-4">
                                <p className="text-[13px] font-semibold uppercase text-black">
                                  {lang === "AR" ? "الكمية" : "Quantity"}
                                </p>

                                <div className="mt-2 flex items-center gap-2">
                                  <button
                                    type="button"
                                    disabled={editQty <= 1}
                                    onClick={() =>
                                      setEditQty((q) =>
                                        Math.max(1, Number(q || 1) - 1)
                                      )
                                    }
                                    className="w-[38px] h-[34px] border border-black text-[20px] disabled:opacity-40"
                                  >
                                    -
                                  </button>

                                  <input
                                    type="number"
                                    min="1"
                                    max={Number(editVariant?.stock_qty || 1)}
                                    value={editQty}
                                    onChange={(e) => {
                                      const max = Number(editVariant?.stock_qty || 1);
                                      const value = Math.min(
                                        Math.max(1, Number(e.target.value || 1)),
                                        max
                                      );
                                      setEditQty(value);
                                    }}
                                    className="w-[64px] h-[34px] border border-black text-center text-[15px] outline-none"
                                  />

                                  <button
                                    type="button"
                                    disabled={
                                      !editVariant ||
                                      editQty >= Number(editVariant.stock_qty || 0)
                                    }
                                    onClick={() => {
                                      const max = Number(editVariant?.stock_qty || 1);
                                      setEditQty((q) =>
                                        Math.min(max, Number(q || 1) + 1)
                                      );
                                    }}
                                    className="w-[38px] h-[34px] border border-black text-[20px] disabled:opacity-40"
                                  >
                                    +
                                  </button>
                                </div>

                                {editVariant && (
                                  <p className="mt-2 text-[12px] text-gray-500">
                                    {lang === "AR" ? "المتوفر" : "Available"}:{" "}
                                    {Number(editVariant.stock_qty || 0)}
                                  </p>
                                )}
                              </div>

                              {editError && (
                                <p className="mt-3 text-[13px] text-red-600 font-semibold">
                                  {editError}
                                </p>
                              )}

                              <div className="mt-5 flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => setEditItemId(null)}
                                  className="h-[40px] px-8 border border-black text-black text-[14px]"
                                >
                                  {lang === "AR" ? "إلغاء" : "Cancel"}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => applyCartItemEdit(item)}
                                  className="h-[40px] px-8 bg-black text-white text-[14px]"
                                >
                                  {lang === "AR" ? "حفظ" : "Save"}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* PRICE */}
                    <div className="pt-4 text-right">
                      {pricesSalesVisible && hasSale && (
  <span className="inline-flex bg-[#c40012] text-white text-[12px] px-1.5 py-1 mb-2 rounded-lg">
    -{getItemDisplayDiscountPercent(item)}%
  </span>
)}

                    <p className="text-[15px] font-semibold">
  {!pricesSalesVisible
    ? t.priceHidden
    : item.isBxgyFree
    ? lang === "AR"
      ? "مجاني"
      : "FREE"
    : formatPrice(getCartItemFinalTotal(item))}
</p>

           {pricesSalesVisible && hasSale &&
  getCartItemCompareTotal(item) >
    getCartItemFinalTotal(item) && (
    <p className="mt-1 text-[13px] line-through">
      {formatPrice(getCartItemCompareTotal(item))}
    </p>
  )}

  {pricesSalesVisible && !item.isBxgyFree && Number(item.qty || 1) > 1 && (
  <p className="mt-2 text-[12px] text-[#777] leading-[1.4]">
    {lang === "AR" ? "لكل قطعة" : "Per piece"}:{" "}
    {formatPrice(Number(item.price || 0))}
  </p>
)}
                    </div>
                  </div>
                );
              })}
            </div>

<CartBxgySuggestions desktop />

            <div className="mt-8 bg-[#f7f7f7] px-5 py-3 flex items-center gap-3 text-[13px] text-[#555]">
              <span className="text-[15px]">ℹ</span>
              <p>
                {lang === "AR"
                  ? "لا تؤجل عملية الشراء، إضافة المنتجات إلى السلة لا تعني أنها محجوزة لك."
                  : "Don't wait with your purchase, adding products to your bag does not mean that they are reserved."}
              </p>
            </div>
          </div>

          {/* RECOMMENDED CARD — moved under products in left column */}
          {recommendedProducts.length > 0 && (
            <div className="px-6 py-4">
              <h2 className="text-[24px] font-bold text-[#333] mb-2">
                {lang === "AR"
                  ? "قد يعجبك أيضاً"
                  : "You may also be interested"}
              </h2>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => scrollRecommended("prev")}
                  className="
                    absolute left-0 top-[35%] z-20
                    w-[24px] h-[48px]
                    bg-white/90
                    text-black
                    text-[30px]
                    flex items-center justify-center
                    shadow-sm
                  "
                >
                  ‹
                </button>

                <div
                  ref={recommendedRailRef}
                  className="flex gap-3 overflow-x-auto no-scrollbar scroll-smooth"
                >
                  {recommendedProducts.map((product) => {
                    const hasSale = Number(product.discount || 0) > 0;
                    const final = getRecommendedFinalPrice(product);
                    const original = Number(product.price || 0);
                    const media = resolveRecommendedMedia(product);
                    const mediaIsVideo = isRecommendedVideo(product);

                    return (
                      <div
  key={product.id}
  role="button"
  tabIndex={0}
  onClick={() => goToRecommendedProduct(product)}
  className="group shrink-0 w-[185px] bg-white text-left cursor-pointer"
>
                        <div className="relative bg-[#f5f5f5] overflow-hidden">
                          <button
  type="button"
  onClick={(e) => handleRecommendedAddToCart(e, product)}
  className="
    hidden md:flex
    group/addcart
    absolute bottom-0 left-0 right-0 z-40
    h-11
    items-center justify-center
    bg-black/55
    text-white
    opacity-0
    pointer-events-none
    group-hover:opacity-100
    group-hover:pointer-events-auto
    transition-all duration-300
    overflow-hidden
  "
>
  <span
    className="
      absolute
      text-[13px]
      font-medium
      tracking-[0.18em]
      uppercase
      transition-all duration-300
      opacity-100 translate-y-0
      group-hover/addcart:opacity-0
      group-hover/addcart:-translate-y-3
    "
  >
    {shouldCheckRecommendedAvailability(product)
      ? lang === "AR"
        ? "تحقق من التوفر"
        : "Check availability"
      : recommendedAddingId === product.id
      ? lang === "AR"
        ? "جارٍ الإضافة"
        : "Adding"
      : lang === "AR"
      ? "أضف إلى السلة"
      : "Add to cart"}
  </span>

  <svg
    className={`
      opacity-0 translate-y-3
      transition-all duration-300
      ${
        shouldCheckRecommendedAvailability(product)
          ? ""
          : "group-hover/addcart:opacity-100 group-hover/addcart:translate-y-0"
      }
    `}
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h8.8a2 2 0 0 0 2-1.6L23 6H6" />
  </svg>
</button>
                          {hasSale && (
                            <span className="absolute top-3 right-0 z-10 bg-[#c40012] text-white px-2 py-1 text-[10px]">
                              -{Number(product.discount)}%
                            </span>
                          )}

                          {mediaIsVideo ? (
                            <video
                              src={media}
                              muted
                              loop
                              autoPlay
                              playsInline
                              preload="metadata"
                              className="w-full h-[245px] object-cover block"
                            />
                          ) : (
                            <img
                              src={media}
                              alt={getRecommendedName(product)}
                              className="w-full h-[245px] object-cover block"
                              loading="lazy"
                            />
                          )}
                        </div>

                        <div className="px-2 pt-3 pb-2">
                          <h3 className="text-[14px] leading-[1.35] text-[#666] font-bold min-h-[38px]">
                            {getRecommendedName(product)}
                          </h3>

                          <div className="mt-4 flex items-center gap-2">
                            <span className="text-[14px] text-black">
                              {formatPrice(final)}
                            </span>

                            {hasSale && (
                              <span className="text-[13px] text-black line-through">
                                {formatPrice(original)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => scrollRecommended("next")}
                  className="
                    absolute right-0 top-[35%] z-20
                    w-[24px] h-[48px]
                    bg-white/90
                    text-black
                    text-[30px]
                    flex items-center justify-center
                    shadow-sm
                  "
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

{/* RIGHT COLUMN */}
<div className="md:sticky md:top-[160px] self-start space-y-8">
        {/* SUMMARY CARD */}
        <aside className="bg-white px-6 py-6">
          <div className="pt-1">
            <h2 className="text-[20px] font-normal text-[#333] mb-3">
              {lang === "AR" ? "الملخص" : "Summary"}
            </h2>

            <div className="space-y-4 text-[14px]">
  <div className="flex items-center justify-between gap-4">
    <span>{lang === "AR" ? "المجموع" : "Subtotal"}</span>
    <span>{pricesSalesVisible ? formatPrice(subtotal) : t.priceHidden}</span>
  </div>

  {effectiveOrderDiscountAmount > 0 && (
    <div className="flex items-center justify-between gap-4 text-green-700">
      <span>{lang === "AR" ? "خصم الطلب" : "Order discount"}</span>
      <span>{pricesSalesVisible ? `-${formatPrice(effectiveOrderDiscountAmount)}` : t.priceHidden}</span>
    </div>
  )}

  {cartCoupon && (
    <div className="flex items-center justify-between gap-4 text-green-700">
      <span>{lang === "AR" ? "كوبون" : "Coupon"}</span>
      <span>{pricesSalesVisible ? `-${formatPrice(couponDiscount)}` : t.priceHidden}</span>
    </div>
  )}

  <div className="flex items-center justify-between gap-4">
    <span>{lang === "AR" ? "توصيل" : "Delivery"}</span>
    <span>
      {deliveryFee === 0
        ? isFreeDeliveryFromDiscount
          ? lang === "AR"
            ? "مجاني (خصم)"
            : "Free (discount)"
          : lang === "AR"
          ? "مجاني"
          : "Free"
        : pricesSalesVisible ? formatPrice(deliveryFee) : t.priceHidden}
    </span>
  </div>

  {freeShippingDiscount && (
    <div
      className={`
        border px-3 py-3 text-[12px] leading-[1.45]
        ${
          isFreeDeliveryFromDiscount
            ? "border-green-200 bg-green-50 text-green-800"
            : "border-orange-200 bg-orange-50 text-orange-800"
        }
      `}
    >
      <div className="font-semibold">
        {freeShippingInfo.label}
      </div>

      {freeShippingInfo.endLabel && (
        <div className="mt-1 opacity-80">
          {lang === "AR"
            ? `ينتهي في ${freeShippingInfo.endLabel}`
            : `Ends on ${freeShippingInfo.endLabel}`}
        </div>
      )}
    </div>
  )}
</div>

        {hasActiveCoupons && (
  <div className="mt-4 border-t border-[#333]">
    <button
      type="button"
      onClick={() => setCouponOpen((prev) => !prev)}
      className="w-full h-[48px] flex items-center gap-2 text-[14px]"
    >
      <ChevronDownIcon open={couponOpen} />
      {lang === "AR" ? "كود الخصم" : "Discount code"}
    </button>

    {couponOpen && (
      <div className="mb-5">
        <div className="grid grid-cols-[1fr_100px] h-[40px]">
          <input
            value={couponInput}
            onChange={(e) => {
              setCouponInput(e.target.value);
              setCouponError(null);
            }}
            className="border border-[#cfcfcf] px-3 text-[14px] outline-none"
          />

          <button
            type="button"
            onClick={applyMobileCoupon}
            disabled={couponLoading}
            className="bg-black text-white text-[14px] disabled:opacity-60"
          >
            {couponLoading ? "..." : lang === "AR" ? "استخدام" : "Use"}
          </button>
        </div>

        {cartCoupon && (
          <div className="mt-3 flex items-center justify-between text-[13px] text-green-700">
            <span>
              {lang === "AR" ? "تم تطبيق الكوبون" : "Coupon applied"}:{" "}
              {cartCoupon.code}
            </span>

            <button
              type="button"
              onClick={clearMobileCoupon}
              className="underline text-black"
            >
              {lang === "AR" ? "إزالة" : "Remove"}
            </button>
          </div>
        )}

        {couponError && (
          <p className="mt-2 text-[13px] text-red-600">
            {couponError === "expired"
              ? lang === "AR"
                ? "انتهت صلاحية الكوبون"
                : "Coupon expired"
              : lang === "AR"
              ? "رمز غير صالح"
              : "Unavailable code"}
          </p>
        )}
      </div>
    )}
  </div>
)}

            <div className="border-t border-[#333] pt-5">

              <div className="flex items-center justify-between text-[15px] font-semibold text-[#333]">
                <span>{lang === "AR" ? "الإجمالي" : "Total"}</span>
                <span>{pricesSalesVisible ? formatPrice(cartSummaryTotal) : t.priceHidden}</span>
              </div>
{!pricesSalesVisible && (
  <div className="mt-4 border border-orange-200 bg-orange-50 text-orange-700 text-[13px] px-4 py-3 rounded-md">
    {t.pricesHiddenCheckout}
  </div>
)}
              <button
                type="button"
                disabled={disableCheckout}
                onClick={goToCheckout}
                className={`
                  mt-6
                  w-full h-[48px]
                  text-white
                  text-[14px]
                  font-normal
                  transition
                  ${disableCheckout ? "bg-gray-400 cursor-not-allowed" : "bg-[#e99a22] hover:opacity-90"}
                `}
              >
                {lang === "AR" ? "الانتقال للدفع »" : "Go to checkout  »"}
              </button>

              {disableCheckout && (
  <p className="mt-3 text-[13px] text-red-600 font-semibold">
    {!pricesSalesVisible
      ? t.pricesHiddenCheckout
      : lang === "AR"
      ? "يرجى تعديل المنتجات المحددة بالأحمر قبل الدفع"
      : "Please fix the red highlighted products before checkout"}
  </p>
)}

              <div className="mt-7 space-y-4 text-[14px]">
                <div className="flex items-center gap-3">
                  <TruckIcon />
                  <span>
                    {shipping.free_enabled && Number(shipping.free_threshold) > 0
                      ? `${lang === "AR" ? "توصيل مجاني من" : "Free delivery from"} ${formatPrice(shipping.free_threshold)}`
                      : lang === "AR"
                      ? "توصيل متوفر"
                      : "Delivery available"}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <ShieldIcon />
                  <span>
                    {lang === "AR"
                      ? "تسوق آمن عبر الإنترنت"
                      : "Safe online shopping"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* HELP CARD — pushed to the bottom */}
        <aside className="bg-white px-6 py-7">
          <h3 className="text-[17px] font-normal text-[#333]">
            {lang === "AR"
              ? "كيف يمكننا مساعدتك؟"
              : "Can we help you somehow?"}
          </h3>

          <div className="mt-5 border-t border-[#d5d5d5] pt-6 flex items-start gap-4">
            <PhoneIcon />

            <div>
              <p className="text-[13px] text-[#777]">
                {lang === "AR" ? "اتصل بنا:" : "Call us:"}
              </p>

              <p className="mt-1 text-[18px] font-semibold text-[#333]">
                81 446 356
              </p>

              <p className="mt-3 text-[13px] leading-[1.6] text-[#777]">
                {lang === "AR"
                  ? "خدمة العملاء متاحة من الإثنين إلى الجمعة من 8:00 صباحاً حتى 4:00 مساءً"
                  : "Customer service office is open from Monday to Friday from 8:00 a.m. to 4:00 p.m"}
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</section>

      <Footer />
    </>
  )}
</div>
{/* DESKTOP RECOMMENDED ADDED TO CART MODAL */}
{recommendedAddedModal && (
  <div
    className="
      fixed inset-0 z-[999999]
      bg-black/20
      hidden md:flex
      items-center justify-center
      px-4
    "
    onClick={() => setRecommendedAddedModal(null)}
  >
    <div
      dir={lang === "AR" ? "rtl" : "ltr"}
      className="
        w-full max-w-[520px]
        bg-white
        rounded-md
        shadow-[0_18px_55px_rgba(0,0,0,0.25)]
        border border-gray-200
      "
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative px-5 py-4">
        <h3 className="text-[18px] md:text-[20px] font-normal text-gray-900">
          {lang === "AR" ? "تمت الإضافة إلى السلة" : "Added to cart"}
        </h3>

        <button
          type="button"
          onClick={() => setRecommendedAddedModal(null)}
          className="
            absolute top-3 right-3
            w-6 h-6
            rounded-full
            bg-gray-500
            text-white
            flex items-center justify-center
            text-[16px] leading-none
            hover:bg-gray-700
            transition
          "
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="bg-[#f3f3f3] px-5 py-5">
        <div className="grid grid-cols-[110px_1fr] gap-6 items-center">
          <div className="bg-white border border-gray-300 p-1">
            {recommendedAddedModal.isVideo ? (
              <video
                src={recommendedAddedModal.image}
                muted
                playsInline
                preload="metadata"
                className="w-full aspect-square object-cover"
              />
            ) : (
              <img
                src={recommendedAddedModal.image}
                alt=""
                className="w-full aspect-square object-cover"
              />
            )}
          </div>

          <div className="text-[12px] md:text-[13px] text-gray-800 space-y-2">
            <div className="flex justify-between gap-4">
              <span>
                {lang === "AR"
                  ? "عدد المنتجات في سلتك"
                  : "Number of products in your cart"}
              </span>
              <span className="font-semibold">
                {recommendedAddedModal.count}
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span>{lang === "AR" ? "مجموع المنتجات" : "Total products"}</span>
              <span className="font-semibold">
                {formatPrice(recommendedAddedModal.subtotal)}
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span>{lang === "AR" ? "الإجمالي" : "Together"}</span>
              <span className="font-semibold">
                {formatPrice(recommendedAddedModal.total)}
              </span>
            </div>

            {recommendedAddedModal.freeEnabled &&
              recommendedAddedModal.freeThreshold > 0 && (
                <div className="mt-2 bg-[#10b981] text-white px-3 py-2 text-[11px] md:text-[12px] font-semibold">
                  {recommendedAddedModal.missingForFree > 0 ? (
                    <div className="flex justify-between gap-3">
                      <span>
                        {lang === "AR"
                          ? "ينقصك للحصول على توصيل مجاني"
                          : "You are missing something for free delivery"}
                      </span>
                      <span>
                        {formatPrice(recommendedAddedModal.missingForFree)}
                      </span>
                    </div>
                  ) : (
                    <span>
                      {lang === "AR"
                        ? "لقد حصلت على توصيل مجاني"
                        : "Free delivery unlocked"}
                    </span>
                  )}
                </div>
              )}
          </div>
        </div>
      </div>

      <div className="px-5 py-5">
        <div className="flex items-center justify-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => setRecommendedAddedModal(null)}
            className="
              flex-1
              h-11
              border border-gray-700
              bg-white
              text-gray-700
              text-[13px]
              hover:bg-gray-50
              transition
            "
          >
            {lang === "AR" ? "العودة إلى المتجر" : "Back to the store"}
          </button>

          <button
            type="button"
            onClick={() => {
              apiRequest("/analytics/track", {
                method: "POST",
                body: JSON.stringify({
                  action_type: "open_cart_page",
                  entity_type: "cart",
                }),
              }).catch(() => {});

              setRecommendedAddedModal(null);
              navigate("/cart");
            }}
            className="
              flex-1
              h-11
              bg-[#e5ad20]
              text-white
              text-[13px]
              font-medium
              hover:bg-[#d59d17]
              transition
            "
          >
            {lang === "AR" ? "اذهب إلى السلة »" : "Go to cart »"}
          </button>
        </div>

        <p className="mt-5 text-[12px] md:text-[13px] text-gray-600 leading-relaxed">
          {lang === "AR"
            ? "لا تؤجل عملية الشراء، إضافة المنتجات إلى السلة لا تعني أنها محجوزة لك."
            : "Don't delay your purchase, adding items to your cart does not mean you have reserved them."}
        </p>
      </div>
    </div>
  </div>
)}
  </>
);
}

export default CartPage;
